/**
 * SelectPersonStep Component
 * Person selection UI
 */

import React, { useMemo, useCallback } from 'react';
import { Users } from 'lucide-react';
import { Label } from '../../label.js';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../select.js';
import { BookingData, PERSON_OPTIONS, DURATION_OPTIONS } from '../utils/bookingConstants.js';

interface SelectPersonStepProps {
  bookingData: BookingData;
  onBookingDataChange: (updates: Partial<BookingData>) => void;
}

export function SelectPersonStep({ bookingData, onBookingDataChange }: SelectPersonStepProps) {
  // Memoize person options to prevent unnecessary re-renders
  const personOptions = useMemo(() => {
    const baseOptions: Array<(typeof PERSON_OPTIONS)[number]> = PERSON_OPTIONS.filter(
      (option) => option.value !== 'miclainkeffeler@westtn.consulting'
    );

    // Add Miclain only if he's currently selected (for query string access)
    if (bookingData.bookeeEmail === 'miclainkeffeler@westtn.consulting') {
      const miclainOption = PERSON_OPTIONS.find(
        (option) => option.value === 'miclainkeffeler@westtn.consulting'
      );

      if (miclainOption) {
        baseOptions.push(miclainOption);
      }
    }

    return baseOptions;
  }, [bookingData.bookeeEmail]);

  // Memoize duration options to prevent unnecessary re-renders
  const durationOptions = useMemo(() => DURATION_OPTIONS, []);

  const handlePersonChange = useCallback(
    (value: string) => {
      onBookingDataChange({ bookeeEmail: value });
    },
    [onBookingDataChange]
  );

  const handleDurationChange = useCallback(
    (value: string) => {
      onBookingDataChange({ meetingDuration: parseInt(value) });
    },
    [onBookingDataChange]
  );

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <Users className="w-16 h-16 mx-auto mb-4 text-blue-600" />
        <h3 className="text-lg font-semibold text-card-foreground mb-2">
          Who would you like to meet with?
        </h3>
        <p className="text-gray-600">Select from our team members below</p>
      </div>

      <div className="space-y-3">
        <Label htmlFor="person-select" className="text-sm font-medium text-gray-700">
          Team Member
        </Label>
        <Select value={bookingData.bookeeEmail} onValueChange={handlePersonChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select a team member" />
          </SelectTrigger>
          <SelectContent>
            {personOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        <Label htmlFor="duration-select" className="text-sm font-medium text-gray-700">
          Meeting Duration
        </Label>
        <Select value={bookingData.meetingDuration.toString()} onValueChange={handleDurationChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select duration" />
          </SelectTrigger>
          <SelectContent>
            {durationOptions.map((option) => (
              <SelectItem key={option.value} value={option.value.toString()}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
